import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CircuitBackground from '../components/CircuitBackground';
import {
  Zap, Cpu, FolderOpen,
  ArrowRight, ChevronRight, Activity, Lightbulb,
  FlaskConical, Shield, Terminal
} from 'lucide-react';

const colorMap: Record<string, { text: string; bg: string; border: string; glow: string }> = {
  green: {
    text: 'var(--color-primary)',
    bg: 'rgba(0,255,136,0.08)',
    border: 'rgba(0,255,136,0.2)',
    glow: '0 0 20px rgba(0,255,136,0.15)',
  },
  cyan: {
    text: 'var(--color-secondary)',
    bg: 'rgba(0,207,255,0.08)',
    border: 'rgba(0,207,255,0.2)',
    glow: '0 0 20px rgba(0,207,255,0.15)',
  },
  amber: {
    text: 'var(--color-accent)',
    bg: 'rgba(255,170,0,0.08)',
    border: 'rgba(255,170,0,0.2)',
    glow: '0 0 20px rgba(255,170,0,0.15)',
  },
  purple: {
    text: 'var(--color-purple)',
    bg: 'rgba(168,85,247,0.08)',
    border: 'rgba(168,85,247,0.2)',
    glow: '0 0 20px rgba(168,85,247,0.15)',
  },
};

const features = [
  {
    icon: Cpu,
    title: 'Diagnosis',
    description: 'Describe your electronics problem and get step-by-step troubleshooting guidance.',
    color: 'green',
    path: '/diagnose',
    label: 'Start Diagnosis',
  },
  /*
  {
    icon: Layers,
    title: 'Parts',
    description: 'Find resistors, ICs, transistors, and more. Instant datasheet lookup.',
    color: 'cyan',
    path: '/components',
    label: 'Find Parts',
  },
  */
  {
    icon: Activity,
    title: 'Studio (BETA)',
    description: 'The AI Workbench for Arduino/ESP32 code and circuit designs.',
    color: 'amber',
    path: '/studio',
    label: 'Open Studio',
  },
  {
    icon: FolderOpen,
    title: 'Maker Projects',
    description: 'Step-by-step DIY electronics builds — Arduino, ESP32, and beyond.',
    color: 'purple',
    path: '/projects',
    label: 'Explore Projects',
  },
  {
    icon: FlaskConical,
    title: 'Good Recipes',
    description: '1-Click curated packs: Code, Wiring, n8n Flow, and BOM.',
    color: 'amber',
    path: '/recipes',
    label: 'View Recipes',
  },
];


const tips = [
  { icon: Lightbulb, text: 'Always discharge capacitors before working on power electronics' },
  { icon: Activity, text: 'Use a multimeter on resistor ranges first to avoid damage' },
  { icon: Shield, text: 'Anti-static wrist straps protect sensitive ICs from ESD damage' },
  { icon: FlaskConical, text: 'Flux helps solder flow — clean joints are stronger joints' },
];

const quickProjects = [
  { title: 'Arduino LED Matrix', difficulty: 'Beginner', tag: 'Arduino', color: 'green' },
  { title: 'ESP32 WiFi Weather Station', difficulty: 'Intermediate', tag: 'ESP32', color: 'cyan' },
  { title: 'Raspberry Pi Media Server', difficulty: 'Intermediate', tag: 'RPi', color: 'purple' },
  { title: 'DIY LiPo Charger Circuit', difficulty: 'Advanced', tag: 'Circuit', color: 'amber' },
];

import SEO from '../components/SEO';

export default function Dashboard() {
  const navigate = useNavigate();
  const [sandboxQuery, setSandboxQuery] = useState('');

  const handleSandboxSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sandboxQuery.trim()) return;
    navigate('/studio', { state: { initialQuery: sandboxQuery } });
  };

  return (
    <div style={{ overflowX: 'hidden' }}>
      <SEO 
        title="DIYElectronicMech | AI Electronics Diagnostics & Maker Hub" 
        description="Your AI-powered electronics workbench. Troubleshoot circuits, find component datasheets, generate schematics, and explore DIY maker projects with ElectrAI."
      />

      {/* ── Hero ── */}
      <section style={{
        minHeight: '88vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '4rem 1.5rem 3rem',
        overflow: 'hidden',
      }}>
        <CircuitBackground />
        {/* Radial glow backgrounds */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          top: '40%',
          left: '20%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(0,207,255,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute',
          top: '30%',
          right: '15%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(255,170,0,0.03) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '860px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Status badge */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(0,255,136,0.08)',
              border: '1px solid rgba(0,255,136,0.25)',
              borderRadius: '999px',
              padding: '0.35rem 1rem',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'var(--color-primary)',
              letterSpacing: '0.05em',
              fontFamily: 'var(--font-mono)',
            }}>
              <span className="status-dot online" />
              ElectrAI ONLINE · AI Electronics Assistant
            </div>
          </div>

          {/* Main headline */}
          <h1 style={{
            fontSize: 'clamp(2.2rem, 6vw, 4.5rem)',
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: '1.25rem',
            letterSpacing: '-0.02em',
          }}>
            <span style={{
              color: 'transparent',
              WebkitTextStroke: '1px #e8f0fe',
              opacity: 0.9,
            }}>
              Your AI Electronics
            </span>
            <br />
            <span style={{
              color: 'transparent',
              WebkitTextStroke: '1.5px var(--color-primary)',
            }}>
              Repair & Maker Hub
            </span>
          </h1>

          {/* Subheadline */}
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
            color: 'var(--text-secondary)',
            maxWidth: '680px',
            margin: '0 auto 2.5rem',
            lineHeight: 1.7,
          }}>
            Diagnose broken electronics, identify components, explore circuit schematics,
            and build amazing maker projects — all with AI at your side.
          </p>
           {/* Sandbox CTA */}
          <div style={{ maxWidth: '600px', margin: '0 auto 3rem' }}>
            <form onSubmit={handleSandboxSubmit} style={{
              display: 'flex',
              background: 'rgba(5, 9, 18, 0.6)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 255, 136, 0.3)',
              borderRadius: '12px',
              padding: '0.5rem',
              boxShadow: '0 0 30px rgba(0,255,136,0.1)',
              transition: 'all 0.3s ease',
            }}>
              <input 
                type="text"
                value={sandboxQuery}
                onChange={e => setSandboxQuery(e.target.value)}
                placeholder="Ask ElectrAI anything... (e.g. 'Why is my ESP32 resetting?')"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'var(--color-primary)',
                  color: '#050912',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#00cc6a'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'var(--color-primary)'}
              >
                <Zap size={18} /> Try it Out
              </button>
            </form>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.25rem' }}>
              <Link to="/projects" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textDecoration: 'none' }}>
                Browse Projects <ArrowRight size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
              </Link>
            </div>
          </div>

          {/* Mini Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2.5rem',
            flexWrap: 'wrap',
          }}>
            {[
              { value: '50K+', label: 'Parts' },
              { value: '12K+', label: 'Diagrams' },
              { value: '200+', label: 'Projects' },
              { value: '24/7', label: 'AI Online' },
            ].map(({ value, label }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-primary)',
                  textShadow: '0 0 16px rgba(0,255,136,0.4)',
                }}>
                  {value}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', letterSpacing: '0.06em' }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="divider-green" style={{ margin: '0 1.5rem' }} />

      {/* ── Feature Cards ── */}
      <section className="section page-container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.75rem' }}>
            Everything a <span className="glow-text-green">maker</span> needs
          </h2>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '520px', margin: '0 auto' }}>
            Five powerful tools — one platform. Built for electronics enthusiasts at every level.
          </p>
        </div>

        <div 
          className="mobile-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1rem',
          }}
        >

          {features.map(({ icon: Icon, title, description, color, path, label }) => {
            const c = colorMap[color];
            return (
              <Link
                key={path}
                to={path}
                style={{
                  background: 'var(--bg-surface)',
                  border: `1px solid var(--border-color)`,
                  borderRadius: '14px',
                  padding: '1.75rem',
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  transition: 'all 0.25s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = c.border;
                  el.style.boxShadow = c.glow;
                  el.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = 'var(--border-color)';
                  el.style.boxShadow = 'none';
                  el.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  width: '46px',
                  height: '46px',
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon size={22} color={c.text} />
                </div>

                <div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                    {title}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {description}
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: c.text,
                  marginTop: 'auto',
                }}>
                  {label} <ChevronRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Quick Projects ── */}
      <section style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', padding: '3.5rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                Featured <span className="glow-text-cyan">Projects</span>
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Jump into a hands-on build</p>
            </div>
            <Link to="/projects" className="btn btn-ghost btn-sm">
              All Projects <ChevronRight size={14} />
            </Link>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
          }}>
            {quickProjects.map(({ title, difficulty, tag, color }) => {
              const c = colorMap[color];
              return (
                <Link
                  key={title}
                  to="/projects"
                  style={{
                    background: 'var(--bg-surface-2)',
                    border: `1px solid var(--border-color)`,
                    borderRadius: '12px',
                    padding: '1.25rem',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = c.border;
                    el.style.boxShadow = c.glow;
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = 'var(--border-color)';
                    el.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <span style={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      background: c.bg,
                      color: c.text,
                      border: `1px solid ${c.border}`,
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.05em',
                    }}>
                      {tag}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{difficulty}</span>
                  </div>
                  <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Maker Tips ── */}
      <section className="section page-container">
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            Quick <span className="glow-text-amber">Maker Tips</span>
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Tricks that every electronics maker should know
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '4rem' }}>
          {tips.map(({ icon: Icon, text }) => (
            <div key={text} style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '1.25rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1rem',
              transition: 'border-color 0.2s ease',
            }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,170,0,0.3)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
            >
              <div style={{
                width: '38px',
                height: '38px',
                background: 'rgba(255,170,0,0.1)',
                border: '1px solid rgba(255,170,0,0.25)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={18} color="var(--color-accent)" />
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{ padding: '4rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,255,136,0.06), rgba(0,207,255,0.06))',
            border: '1px solid rgba(0,255,136,0.2)',
            borderRadius: '20px',
            padding: 'clamp(2rem, 5vw, 4rem)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 50% 0%, rgba(0,255,136,0.08) 0%, transparent 60%)',
              pointerEvents: 'none',
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}>
                <Terminal size={16} color="var(--color-primary)" />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--color-primary)' }}>
                  ElectrAI_v1.0 {'>'} ready
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.4rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: '1rem' }}>
                Ready to fix or build something?
              </h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto 2rem', fontSize: '0.95rem' }}>
                Describe your electronics challenge and let ElectrAI guide you to the solution.
              </p>
              <Link
                to="/diagnose"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'linear-gradient(135deg, #00ff88, #00cc6a)',
                  color: '#050912',
                  padding: '0.875rem 2.5rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 0 28px rgba(0,255,136,0.4)',
                  transition: 'all 0.25s ease',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 44px rgba(0,255,136,0.6)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 28px rgba(0,255,136,0.4)';
                }}
              >
                <Zap size={18} /> Launch ElectrAI <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid var(--border-color)',
        padding: '2rem 1.5rem',
        background: 'var(--bg-surface)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Zap size={16} color="var(--color-primary)" />
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              DIYElectronicMech
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              © {new Date().getFullYear()}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { label: 'About', path: '/about' },
              { label: 'FAQ', path: '/faq' },
              { label: 'Privacy', path: '/privacy' },
              { label: 'Terms', path: '/terms' },
              { label: 'Upgrade', path: '/upgrade' },
            ].map(({ label, path }) => (
              <Link key={label} to={path} style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textDecoration: 'none' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-primary)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'}
              >
                {label}
              </Link>
            ))}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            Fix it. Build it. Understand it.
          </div>
        </div>
      </footer>

    </div>
  );
}
